import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, ArrowLeft, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhone } from '@/utils/formatPhone';
import { PET_COLORS, parseColorField } from '@/utils/petColors';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon in coral color
const coralIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="10" fill="#FF8C66" stroke="#FF7A50" stroke-width="2"/>
      <circle cx="16" cy="16" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Component to update map center when position changes
function MapCenterUpdater({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);

  return null;
}

// Component to handle map clicks
function LocationMarker({ position, setPosition, setLocationText }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      setLocationText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      toast.success('📍 Ubicación seleccionada');
    },
  });

  return position ? (
    <Marker
      position={position}
      icon={coralIcon}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const { lat, lng } = e.target.getLatLng();
          setPosition([lat, lng]);
          setLocationText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          toast.success('📍 Ubicación actualizada');
        },
      }}
    />
  ) : null;
}

const PostPet = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { petId } = useParams();
  const isEdit = !!petId;
  const [type, setType] = useState(searchParams.get('type') || 'lost');
  const [petType, setPetType] = useState(''); // 'dog' or 'cat'
  const [name, setName] = useState('');
  const [color, setColor] = useState([]);
  const [position, setPosition] = useState([-31.4201, -64.1888]); // Default: Córdoba, Argentina
  const [locationText, setLocationText] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState('');
  const [countryCode, setCountryCode] = useState('+54');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [loadingPet, setLoadingPet] = useState(isEdit);

  // Fecha de hoy en formato YYYY-MM-DD (tope para el campo de fecha; no se permiten fechas futuras)
  const today = new Date().toLocaleDateString('en-CA');

  // Function to get user location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }

    setLocationLoading(true);
    toast.loading('Obteniendo tu ubicación...', { id: 'location' });

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = [pos.coords.latitude, pos.coords.longitude];
        setPosition(newPos);
        setLocationText(`${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`);
        setLocationLoading(false);
        toast.success('📍 Ubicación obtenida: ' + pos.coords.latitude.toFixed(4) + ', ' + pos.coords.longitude.toFixed(4), { id: 'location' });
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationLoading(false);
        let errorMessage = 'Error al obtener ubicación';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado. Habilítalo en tu navegador.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Información de ubicación no disponible';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tiempo de espera agotado al obtener ubicación';
            break;
        }

        toast.error(errorMessage, { id: 'location' });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  // Al montar: en modo edición precargamos la publicación; al crear, geolocalizamos.
  useEffect(() => {
    if (isEdit) {
      const loadPet = async () => {
        try {
          const res = await axios.get(`${API}/pets/${petId}`, { withCredentials: true });
          const pet = res.data;
          setType(pet.type || 'lost');
          setPetType(pet.pet_type || '');
          setName(pet.name || '');
          setColor(parseColorField(pet.color));
          if (pet.latitude != null && pet.longitude != null) {
            setPosition([pet.latitude, pet.longitude]);
          }
          setLocationText(pet.location || '');
          setDate(pet.date_lost_found || '');
          setDescription(pet.description || '');
          setPhotos(pet.photos || []);
          const cc = pet.country_code || '+54';
          setCountryCode(cc);
          let number = pet.contact || '';
          if (number.startsWith(cc)) number = number.slice(cc.length).trim();
          setContact(formatPhone(number));
        } catch (err) {
          toast.error('No se pudo cargar la publicación');
          navigate('/dashboard');
        } finally {
          setLoadingPet(false);
        }
      };
      loadPet();
    } else {
      getUserLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let rejected = false;
    files.forEach(file => {
      if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
        validFiles.push(file);
      } else {
        rejected = true;
      }
    });
    if (rejected) {
      toast.error('Solo se permiten imágenes JPEG, PNG o WebP');
    }
    validFiles.slice(0, 5 - photos.length).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => setPhotos(prev => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  // Handle manual coordinate input
  const handleLocationTextChange = (e) => {
    const text = e.target.value;
    setLocationText(text);

    // Try to parse coordinates from text (format: "lat, lng" or "lat,lng")
    const coordPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/;
    const match = text.match(coordPattern);

    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);

      // Validate coordinates
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setPosition([lat, lng]);
        toast.success('📍 Mapa actualizado con coordenadas');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!petType || color.length === 0 || !locationText || !date || !description || !contact || photos.length === 0) {
      toast.error('Completa todos los campos obligatorios (incluyendo color y teléfono) y agrega al menos una foto');
      return;
    }

    if (date > today) {
      toast.error('La fecha no puede ser futura');
      return;
    }

    setUploading(true);
    const payload = {
      type,
      pet_type: petType, // 'dog' or 'cat'
      name,
      color,
      location: locationText,
      latitude: position[0],
      longitude: position[1],
      date_lost_found: date,
      description,
      contact: contact ? `${countryCode} ${contact}` : null,
      country_code: countryCode,
      photos
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/pets/${petId}`, payload, { withCredentials: true });
        toast.success('Publicación actualizada');
        navigate(`/pet/${petId}`);
      } else {
        const res = await axios.post(`${API}/pets`, payload, { withCredentials: true });
        toast.success('¡Publicación creada exitosamente!');
        navigate(`/pet/${res.data.pet_id}`);
      }
    } catch (err) {
      toast.error(isEdit ? 'Error al actualizar publicación' : 'Error al crear publicación');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  if (loadingPet) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-mesh">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh">
      <header className="bg-white/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="rounded-full">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Heart className="h-6 w-6 text-primary" fill="currentColor" />
          <h1 className="text-xl font-fraunces font-semibold">
            {isEdit ? 'Editar Publicación' : type === 'lost' ? 'Reportar Perdida' : 'Reportar Encontrada'}
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="rounded-3xl border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lost">Perdida</SelectItem>
                  <SelectItem value="found">Encontrada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fotos *</Label>
              <label className="block border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer hover:border-primary transition-colors">
                <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
                <p className="text-sm text-muted-foreground">Haz clic para agregar fotos (máx 5)</p>
              </label>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative group">
                      <img src={p} alt="" className="w-full h-24 object-cover rounded-xl" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-1 right-1 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={15} className="rounded-xl" placeholder="Ej: Max, Luna" />
            </div>

            <div>
              <Label>Tipo de Animal *</Label>
              <Select value={petType} onValueChange={setPetType}>
                <SelectTrigger className="rounded-xl" data-testid="pet-type-select">
                  <SelectValue placeholder="Selecciona tipo de animal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dog">🐕 Perro</SelectItem>
                  <SelectItem value="cat">🐈 Gato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Color *</Label>
              <ToggleGroup
                type="multiple"
                value={color}
                onValueChange={setColor}
                className="flex flex-wrap gap-2 justify-start mt-1"
              >
                {PET_COLORS.map((c) => (
                  <ToggleGroupItem
                    key={c.value}
                    value={c.value}
                    className="rounded-full px-3 py-1 text-sm border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
                  >
                    {c.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div>
              <Label className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-primary" />
                Ubicación * (Haz clic en el mapa o arrastra el pin)
              </Label>

              <Button
                type="button"
                onClick={getUserLocation}
                disabled={locationLoading}
                variant="outline"
                className="w-full rounded-xl mb-2"
              >
                {locationLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                    Obteniendo ubicación...
                  </>
                ) : (
                  <>📍 Usar Mi Ubicación Actual</>
                )}
              </Button>

              <div className="rounded-2xl overflow-hidden border-2 border-border mb-2 h-[300px] sm:h-[400px] w-full">
                <MapContainer
                  center={position}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenterUpdater center={position} />
                  <LocationMarker
                    position={position}
                    setPosition={setPosition}
                    setLocationText={setLocationText}
                  />
                </MapContainer>
              </div>

              <Input
                value={locationText}
                onChange={handleLocationTextChange}
                placeholder="Ingresa coordenadas (ej: 40.416775, -3.703790) o dirección"
                maxLength={200}
                className="rounded-xl"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                💡 Usa el botón para tu ubicación, ingresa coordenadas manualmente, o haz clic en el mapa
              </p>
            </div>

            <div>
              <Label htmlFor="date">{type === 'lost' ? 'Fecha de Pérdida *' : 'Fecha de Encuentro *'}</Label>
              <Input id="date" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" required />
            </div>

            <div>
              <Label htmlFor="description">Descripción *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                className="rounded-xl min-h-24"
                placeholder="Describe características distintivas, comportamiento, última vez visto, etc."
                required
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{description.length}/2000</p>
            </div>

            <div>
              <Label htmlFor="contact">Teléfono de Contacto *</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="w-full sm:w-[180px]">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <span>
                            {countryCode === '+1' ? '🇺🇸' : countryCode === '+52' ? '🇲🇽' : countryCode === '+34' ? '🇪🇸' : countryCode === '+54' ? '🇦🇷' : countryCode === '+56' ? '🇨🇱' : countryCode === '+57' ? '🇨🇴' : countryCode === '+51' ? '🇵🇪' : countryCode === '+58' ? '🇻🇪' : countryCode === '+593' ? '🇪🇨' : '🇧🇷'}
                          </span>
                          <span>{countryCode}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+1"><div className="flex items-center gap-2"><span>🇺🇸</span><span>+1 USA/Canadá</span></div></SelectItem>
                      <SelectItem value="+52"><div className="flex items-center gap-2"><span>🇲🇽</span><span>+52 México</span></div></SelectItem>
                      <SelectItem value="+34"><div className="flex items-center gap-2"><span>🇪🇸</span><span>+34 España</span></div></SelectItem>
                      <SelectItem value="+54"><div className="flex items-center gap-2"><span>🇦🇷</span><span>+54 Argentina</span></div></SelectItem>
                      <SelectItem value="+56"><div className="flex items-center gap-2"><span>🇨🇱</span><span>+56 Chile</span></div></SelectItem>
                      <SelectItem value="+57"><div className="flex items-center gap-2"><span>🇨🇴</span><span>+57 Colombia</span></div></SelectItem>
                      <SelectItem value="+51"><div className="flex items-center gap-2"><span>🇵🇪</span><span>+51 Perú</span></div></SelectItem>
                      <SelectItem value="+58"><div className="flex items-center gap-2"><span>🇻🇪</span><span>+58 Venezuela</span></div></SelectItem>
                      <SelectItem value="+593"><div className="flex items-center gap-2"><span>🇪🇨</span><span>+593 Ecuador</span></div></SelectItem>
                      <SelectItem value="+55"><div className="flex items-center gap-2"><span>🇧🇷</span><span>+55 Brasil</span></div></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  id="contact"
                  type="tel"
                  value={contact}
                  onChange={(e) => setContact(formatPhone(e.target.value))}
                  placeholder="Ej: 351 123 4567"
                  maxLength={100}
                  className="rounded-xl flex-1"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full rounded-full bg-primary text-primary-foreground py-6 text-lg hover:bg-primary/90 transition-colors"
            >
              {isEdit
                ? (uploading ? 'Guardando...' : 'Guardar cambios')
                : (uploading ? 'Publicando...' : 'Publicar')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default PostPet;
